import type { SegmentRow } from './types';

/** Keep focus on an existing stable order, or on the closest following sentence. */
export function normalizeFocusedSegmentOrder(segments: SegmentRow[], current?: number): number | undefined {
  if (!segments.length) return undefined;
  if (current !== undefined && segments.some(row => row[0] === current)) return current;
  if (current === undefined) return segments[0][0];
  return segments.find(row => row[0] > current)?.[0] ?? segments[segments.length - 1][0];
}

export function adjacentFocusedSegmentOrder(segments: SegmentRow[], current: number | undefined, direction: -1 | 1): number | undefined {
  const focused = normalizeFocusedSegmentOrder(segments, current);
  const index = segments.findIndex(row => row[0] === focused);
  if (index < 0) return undefined;
  return segments[Math.min(segments.length - 1, Math.max(0, index + direction))][0];
}

export function toggleSegmentSelection(selected: number[], order: number, checked: boolean): number[] {
  return checked ? [...new Set([...selected, order])].sort((left, right) => left - right) : selected.filter(value => value !== order);
}

/** Deletion resequences orders. Preserve the surviving sentence, then prefer its successor. */
export function focusedOrderAfterDeletion(segments: SegmentRow[], selected: number[], current?: number): number | undefined {
  const removed = new Set(selected);
  const remaining = segments.filter(row => !removed.has(row[0]));
  if (!remaining.length) return undefined;
  const sourceOrder = normalizeFocusedSegmentOrder(remaining, current);
  return remaining.findIndex(row => row[0] === sourceOrder) + 1;
}

export function focusedOrderAfterMerge(segments: SegmentRow[], selected: number[], current?: number): number | undefined {
  const merged = new Set(selected);
  const first = segments.find(row => merged.has(row[0]));
  if (!first) return normalizeFocusedSegmentOrder(segments, current);
  const remaining = segments.filter(row => !merged.has(row[0]) || row === first);
  const target = current !== undefined && merged.has(current) ? first[0] : current;
  const sourceOrder = normalizeFocusedSegmentOrder(remaining, target);
  return remaining.findIndex(row => row[0] === sourceOrder) + 1;
}

export function focusedOrderAfterSplit(segments: SegmentRow[], splitOrder: number, current?: number): number | undefined {
  const focused = normalizeFocusedSegmentOrder(segments, current);
  if (focused === undefined) return undefined;
  const focusedIndex = segments.findIndex(row => row[0] === focused);
  const splitIndex = segments.findIndex(row => row[0] === splitOrder);
  return focusedIndex + 1 + (splitIndex >= 0 && focusedIndex > splitIndex ? 1 : 0);
}
